import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createProject,
  deleteProject,
  getProjectById,
  getProjectBySlug,
  queryProjects,
  rotateProjectApiKey,
  testProjectPipelineWebhook,
  updateProject,
  wipeProjectData,
} from '@/features/projects/data';
import {
  gridPaths,
  invalidateCurrentUserRoles,
  invalidateGridQuery,
  invalidateProjectById,
  invalidateProjectDetail,
  invalidateProjects,
  queryKeys,
  useAccessToken,
  useInvalidateQueries,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import type {
  ProjectApiKeyRotateResult,
  ProjectPipelineWebhookTestResult,
  ProjectWipeDataInput,
  ProjectWipeDataResult,
} from '@/features/projects/data/projects';
import type { ApiError } from '@/shared/data/client';
import type { MetricThresholdGroup, ProjectDto, QueryResult } from '@flowqa/shared';

type CreateProjectInput = {
  name: string;
  badgeChars?: ProjectDto['badgeChars'];
  badgeColor?: ProjectDto['badgeColor'];
  orgId?: string | null;
};
type MetricThresholdUpdate = {
  target: number;
  warn: number;
  enabled?: boolean;
  targetUnit?: string;
  warnUnit?: string;
};
type MetricThresholdGroupUpdate = {
  passRate?: MetricThresholdUpdate;
  frequency?: MetricThresholdUpdate;
  avgDuration?: MetricThresholdUpdate;
  flakeScore?: MetricThresholdUpdate;
  mttf?: MetricThresholdUpdate;
  greenTime?: MetricThresholdUpdate;
};
type ProjectMetricThresholdUpdate = {
  suites?: MetricThresholdGroupUpdate;
  executionRuns?: MetricThresholdGroupUpdate;
  tests?: MetricThresholdGroupUpdate;
  pipelines?: MetricThresholdGroupUpdate;
  stages?: MetricThresholdGroupUpdate;
  jobs?: MetricThresholdGroupUpdate;
};
type UpdateProjectInput = {
  id: string;
  name?: string;
  badgeChars?: ProjectDto['badgeChars'];
  badgeColor?: ProjectDto['badgeColor'];
  projectApiKey?: null;
  metricThresholds?: ProjectMetricThresholdUpdate;
};
type DeleteProjectInput = { id: string };
type TestProjectPipelineWebhookInput = { id: string; apiKey: string };
type RotateProjectApiKeyInput = { id: string; tags?: string[] };

const WIPE_DATA_QUERY_ROOTS = new Set([
  'projects',
  'features',
  'releases',
  'suites',
  'suiteRuns',
  'tests',
  'testRuns',
  'builds',
  'pipelines',
  'uploads',
  'results-files',
  'execution-runs',
  'executionRuns',
  'metrics',
]);

const WIPE_DATA_GRID_PATHS: ReadonlySet<string> = new Set([
  gridPaths.projects,
  gridPaths.features,
  gridPaths.releases,
  gridPaths.releasesRelated,
  gridPaths.suites,
  gridPaths.suiteRuns,
  gridPaths.tests,
  gridPaths.testRuns,
  gridPaths.pipelines,
  gridPaths.builds,
  gridPaths.uploads,
  gridPaths.resultsFiles,
  gridPaths.executionRuns,
]);

const isWipeDataQueryKey = (queryKey: readonly unknown[]): boolean => {
  const root = queryKey[2];
  if (typeof root !== 'string') return false;
  if (WIPE_DATA_QUERY_ROOTS.has(root)) return true;
  if (root !== 'datagrid') return false;
  const path = queryKey[3];
  return typeof path === 'string' && WIPE_DATA_GRID_PATHS.has(path);
};

const mergeMetricThresholdGroup = (
  base: MetricThresholdGroup,
  update?: MetricThresholdGroupUpdate,
): MetricThresholdGroup => {
  if (!update) return base;
  return {
    passRate: { ...base.passRate, ...(update.passRate ?? {}) },
    frequency: { ...base.frequency, ...(update.frequency ?? {}) },
    avgDuration: { ...base.avgDuration, ...(update.avgDuration ?? {}) },
    flakeScore: { ...base.flakeScore, ...(update.flakeScore ?? {}) },
    mttf: { ...base.mttf, ...(update.mttf ?? {}) },
    greenTime: { ...base.greenTime, ...(update.greenTime ?? {}) },
  };
};

export const useProjectsQuery = (options?: {
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
}): UseQueryResult<QueryResult<ProjectDto>, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<QueryResult<ProjectDto>, ApiError>({
    queryKey: queryKeys.projects(orgKey),
    enabled: Boolean(token) && (options?.enabled ?? true),
    queryFn: async (): Promise<QueryResult<ProjectDto>> => {
      const result = await queryProjects(orgForQuery, token);
      if (selectedOrganisationId === null) {
        const items = result.items.filter((proj) => !proj.organisationId);
        return { ...result, items, total: items.length };
      }
      return result;
    },
    staleTime: options?.staleTimeMs ?? 5 * 60_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
  });
};

export const useProjectDetailQuery = (
  slug: string | null,
): UseQueryResult<ProjectDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<ProjectDto | null, ApiError>({
    queryKey: queryKeys.projectDetail(slug, orgKey),
    enabled: Boolean(token && slug),
    queryFn: async (): Promise<ProjectDto | null> =>
      await getProjectBySlug(slug, orgForQuery, token),
    staleTime: 15_000,
  });
};

export const useProjectByIdQuery = (
  projectId: string | null,
  options?: { staleTimeMs?: number; refetchOnWindowFocus?: boolean },
): UseQueryResult<ProjectDto | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  return useQuery<ProjectDto | null, ApiError>({
    queryKey: queryKeys.projectById(projectId, orgKey),
    enabled: Boolean(token && projectId),
    queryFn: async (): Promise<ProjectDto | null> => {
      if (!projectId) return null;
      return await getProjectById(projectId, orgForQuery, token);
    },
    staleTime: options?.staleTimeMs ?? 15_000,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
  });
};

export const useCreateProject = (): UseMutationResult<ProjectDto, ApiError, CreateProjectInput> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();

  return useMutation<ProjectDto, ApiError, CreateProjectInput>({
    mutationFn: async (input: CreateProjectInput): Promise<ProjectDto> => {
      const { orgId, ...payload } = input;
      return await createProject(payload, undefined, orgId ?? undefined);
    },
    onSuccess: async (created) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        upsertIntoResult(prev, created),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<ProjectDto>('flowqa-project-created', { detail: created }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateGridQuery(gridPaths.projects),
        invalidateCurrentUserRoles(),
      ]);
    },
  });
};

export const useUpdateProject = (): UseMutationResult<
  ProjectDto,
  ApiError,
  UpdateProjectInput,
  { previousProjects?: QueryResult<ProjectDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();
  const { selectedOrganisationId } = useOrganisationFilter();

  return useMutation<
    ProjectDto,
    ApiError,
    UpdateProjectInput,
    { previousProjects?: QueryResult<ProjectDto> }
  >({
    mutationFn: async (input: UpdateProjectInput): Promise<ProjectDto> => {
      return await updateProject(input, selectedOrganisationId);
    },
    onMutate: (input) => {
      const previousProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(projectsKey);
      if (previousProjects) {
        queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id
                    ? ((() => {
                        if (!input.metricThresholds) {
                          return { ...item, ...input };
                        }
                        const nextMetricThresholds: ProjectDto['metricThresholds'] = {
                          suites: mergeMetricThresholdGroup(
                            item.metricThresholds.suites,
                            input.metricThresholds.suites,
                          ),
                          executionRuns: mergeMetricThresholdGroup(
                            item.metricThresholds.executionRuns,
                            input.metricThresholds.executionRuns,
                          ),
                          tests: mergeMetricThresholdGroup(
                            item.metricThresholds.tests,
                            input.metricThresholds.tests,
                          ),
                          pipelines: mergeMetricThresholdGroup(
                            item.metricThresholds.pipelines,
                            input.metricThresholds.pipelines,
                          ),
                          stages: mergeMetricThresholdGroup(
                            item.metricThresholds.stages,
                            input.metricThresholds.stages,
                          ),
                          jobs: mergeMetricThresholdGroup(
                            item.metricThresholds.jobs,
                            input.metricThresholds.jobs,
                          ),
                        };
                        return { ...item, ...input, metricThresholds: nextMetricThresholds };
                      })() as ProjectDto)
                    : item,
                ),
              }
            : prev,
        );
      }
      return { previousProjects };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsKey, context.previousProjects);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        upsertIntoResult(prev, updated),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<ProjectDto>('flowqa-project-updated', { detail: updated }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateProjectById(updated.id, selectedOrganisationId ?? null),
        invalidateProjectDetail(updated.slug),
        invalidateGridQuery(gridPaths.projects),
      ]);
    },
  });
};

export const useDeleteProject = (): UseMutationResult<
  { id: string },
  ApiError,
  DeleteProjectInput,
  { previousProjects?: QueryResult<ProjectDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const projectsKey = queryKeys.projects();
  const { selectedOrganisationId } = useOrganisationFilter();

  return useMutation<
    { id: string },
    ApiError,
    DeleteProjectInput,
    { previousProjects?: QueryResult<ProjectDto> }
  >({
    mutationFn: async (input: DeleteProjectInput): Promise<{ id: string }> => {
      return await deleteProject(input);
    },
    onMutate: (input) => {
      const previousProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(projectsKey);
      if (previousProjects) {
        queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
          removeFromResult(prev, input.id),
        );
      }
      return { previousProjects };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(projectsKey, context.previousProjects);
      }
    },
    onSuccess: async (deleted) => {
      queryClient.setQueryData<QueryResult<ProjectDto>>(projectsKey, (prev) =>
        removeFromResult(prev, deleted.id),
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent<{ id: string; organisationId: string | null }>('flowqa-project-removed', {
            detail: { id: deleted.id, organisationId: selectedOrganisationId ?? null },
          }),
        );
      }
      await invalidateQueries([
        invalidateProjects(),
        invalidateProjectById(deleted.id, selectedOrganisationId ?? null),
        invalidateProjectDetail(null, selectedOrganisationId ?? null),
        invalidateGridQuery(gridPaths.projects),
      ]);
    },
  });
};

export const useTestProjectPipelineWebhook = (): UseMutationResult<
  ProjectPipelineWebhookTestResult,
  ApiError,
  TestProjectPipelineWebhookInput
> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<ProjectPipelineWebhookTestResult, ApiError, TestProjectPipelineWebhookInput>({
    mutationFn: async (
      input: TestProjectPipelineWebhookInput,
    ): Promise<ProjectPipelineWebhookTestResult> =>
      await testProjectPipelineWebhook(input, selectedOrganisationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectDetail(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectById(null, selectedOrganisationId ?? null),
        exact: false,
      });
    },
  });
};

export const useRotateProjectApiKey = (): UseMutationResult<
  ProjectApiKeyRotateResult,
  ApiError,
  RotateProjectApiKeyInput
> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  return useMutation<ProjectApiKeyRotateResult, ApiError, RotateProjectApiKeyInput>({
    mutationFn: async (input: RotateProjectApiKeyInput): Promise<ProjectApiKeyRotateResult> =>
      await rotateProjectApiKey(input, selectedOrganisationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectDetail(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projectById(null, selectedOrganisationId ?? null),
        exact: false,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects(), exact: false });
    },
  });
};

export const useWipeProjectData = (): UseMutationResult<
  ProjectWipeDataResult,
  ApiError,
  ProjectWipeDataInput
> => {
  const queryClient = useQueryClient();
  return useMutation<ProjectWipeDataResult, ApiError, ProjectWipeDataInput>({
    mutationFn: async (input: ProjectWipeDataInput): Promise<ProjectWipeDataResult> => {
      return await wipeProjectData(input);
    },
    onSuccess: async (result) => {
      if (result.status === 'wiped') {
        await queryClient.cancelQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
        });
        queryClient.removeQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
        });
        await queryClient.refetchQueries({
          predicate: (query) => isWipeDataQueryKey(query.queryKey),
          type: 'active',
        });
        return;
      }

      await queryClient.invalidateQueries({
        predicate: (query) => isWipeDataQueryKey(query.queryKey),
      });
    },
  });
};

export const useInvalidateProjects = (): (() => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  return useCallback(async (): Promise<void> => {
    await invalidateQueries([invalidateProjects(), invalidateGridQuery(gridPaths.projects)]);
  }, [invalidateQueries]);
};

export const useInvalidateProjectDetail = (): ((slug: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  return useCallback(
    async (slug: string | null): Promise<void> => {
      await invalidateQueries([invalidateProjectDetail(slug)]);
    },
    [invalidateQueries],
  );
};
